import { Injectable } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class AiProviderService {
    constructor(private readonly datatask: DataTaskService) {}

    /**
     * Retrieves the initial AI settings page state.
     * The DataTask takes no parameters.
     *
     * @datatask 4082
     * @datataskName tt_ai_settings_get
     * @returns promise containing main_tab, ai_provider_keyno, ai_provider_id,
     * api_key_label and api_key. Rejects when the DataTask fails or returns no page row.
     */
    public async getAiSettings(): Promise<AiSettingsGet> {
        const page = (await this.post<AiSettingsGet>({ method: 4082 }))[0];
        if (!page) {
            throw new AiProviderError('ai_settings_provider_no_result');
        }

        return page;
    }

    /**
     * Retrieves the provider options available when creating a credential.
     * The DataTask takes no parameters.
     *
     * @datatask 4077
     * @datataskName tt_ai_provider_name_list
     * @returns promise containing rows with ai_provider_id and ai_provider_name.
     */
    public getNames(): Promise<AiProviderName[]> {
        return this.post<AiProviderName>({ method: 4077 });
    }

    /**
     * Retrieves the configured provider credentials without their API keys.
     * The DataTask takes no parameters.
     *
     * @datatask 4078
     * @datataskName tt_ai_provider_list
     * @returns promise containing ai_provider_keyno, ai_provider_id,
     * ai_provider_name, api_key_label and last_synced_datettime for each provider.
     */
    public getProviders(): Promise<AiProvider[]> {
        return this.post<AiProvider>({ method: 4078 });
    }

    /**
     * Encrypts a new credential in the browser and creates its provider entry.
     * The DataTask receives only encrypted_credential; the provider ID, label
     * and API key are included inside the encrypted submission.
     *
     * @datatask 4073
     * @datataskName tt_ai_provider_create
     * @param params the selected ai_provider_id, user-entered api_key_label
     * and api_key. The label is trimmed; the API key contents are preserved.
     * @returns promise containing ai_provider_keyno, api_key_label,
     * credential_ref and state. Nonzero errorcode rejects with errormessage.
     */
    public async createProvider(params: CreateAiProviderParams): Promise<AiProviderAction> {
        const encryptedCredential = await this.encryptCredential({
            ...params,
            replaceProviderKeyno: 0,
        });

        return this.action({
            method: 4073,
            parameters: { encrypted_credential: encryptedCredential },
        });
    }

    /**
     * Fetches the available models for a configured provider.
     *
     * @datatask 4042
     * @datataskName tt_ai_provider_fetch_models
     * @param params the ai_provider_keyno returned when the provider was created.
     * @returns promise resolving after the model fetch completes; rejects
     * with the backend errormessage when the DataTask reports an error.
     */
    public async fetchProviderModels(params: FetchAiProviderModelsParams): Promise<void> {
        await this.post<unknown>({ method: 4042, parameters: params });
    }

    /**
     * Encrypts a replacement API key for an existing provider credential.
     * Fetches a fresh public key from DataTask 4076. The encrypted submission
     * uses the selected row's ai_provider_id and api_key_label, the new API key,
     * and replaceProviderKeyno set to that row's ai_provider_keyno.
     * DataTask 4074 receives only ai_provider_keyno and encrypted_credential.
     *
     * @datatask 4074
     * @datataskName tt_ai_provider_replace
     * @param params the selected ai_provider_keyno, ai_provider_id,
     * read-only api_key_label and replacement api_key to encrypt.
     * @returns promise containing ai_provider_keyno, api_key_label,
     * credential_ref and state. Nonzero errorcode rejects with errormessage.
     */
    public async replaceProvider(params: ReplaceAiProviderParams): Promise<AiProviderAction> {
        const keyno = Number(params.ai_provider_keyno);
        if (!Number.isSafeInteger(keyno) || keyno <= 0) {
            throw new AiProviderError('ai_settings_invalid_provider');
        }

        const encryptedCredential = await this.encryptCredential({
            ...params,
            replaceProviderKeyno: keyno,
        });

        return this.action({
            method: 4074,
            parameters: {
                ai_provider_keyno: params.ai_provider_keyno,
                encrypted_credential: encryptedCredential,
            },
        });
    }

    /**
     * Deletes a configured provider credential.
     *
     * @datatask 4075
     * @datataskName tt_ai_provider_delete
     * @param params the ai_provider_keyno identifying the credential to delete.
     * @returns promise containing ai_provider_keyno, api_key_label,
     * credential_ref and state. Nonzero errorcode rejects with errormessage.
     */
    public deleteProvider(params: DeleteAiProviderParams): Promise<AiProviderAction> {
        return this.action({ method: 4075, parameters: params });
    }

    /**
     * Retrieves a fresh Agent public key for each credential submission.
     * The DataTask takes no parameters.
     *
     * @datatask 4076
     * @datataskName tt_ai_provider_public_key
     * @returns promise containing key_id, key_type, algorithm, key_use,
     * modulus, exponent, company_id and expires_in_seconds, or undefined
     * when the DataTask returns no rows.
     */
    private async getPublicKey(): Promise<AiProviderPublicKey | undefined> {
        return (await this.post<AiProviderPublicKey>({ method: 4076 }))[0];
    }

    /**
     * Executes a provider action and requires one successful response row.
     *
     * @param params the registered provider action DataTask ID and its parameters.
     * @returns promise containing the first provider action response.
     */
    private async action(params: AiProviderDataTaskParams): Promise<AiProviderAction> {
        const action = (await this.post<AiProviderAction>(params))[0];
        if (!action) {
            throw new AiProviderError('ai_settings_provider_no_result');
        }

        return action;
    }

    /**
     * Executes a DataTask and checks the returned rows for backend errors.
     *
     * @param params the registered DataTask ID and its input parameters.
     * @returns promise containing typed rows; nonzero errorcode rejects
     * with the backend errormessage so the caller can display it.
     */
    private async post<T>(params: AiProviderDataTaskParams): Promise<T[]> {
        const rows: (T & { errorcode?: string | number; errormessage?: string })[] = await this.datatask.Post(params.method, params.parameters ?? {}, { suppressErrorMessage: true });
        if (!Array.isArray(rows)) {
            throw new AiProviderError('ai_settings_provider_invalid_result');
        }

        const failure = rows.find((row) => row.errorcode !== undefined && String(row.errorcode) !== '0');
        if (failure) {
            throw failure.errormessage ? new Error(failure.errormessage) : new AiProviderError('ai_settings_provider_error');
        }

        return rows;
    }

    /**
     * Builds and encrypts the submission using a fresh public key.
     *
     * @param params the ai_provider_id, api_key_label, api_key and
     * replaceProviderKeyno (zero for creation, or the existing provider key
     * for replacement). The submission expires within 300 seconds.
     * @returns promise containing the compact JWE sent as encrypted_credential.
     */
    private async encryptCredential(params: EncryptAiProviderCredentialParams): Promise<string> {
        if (!params.ai_provider_id || !params.api_key_label.trim() || !params.api_key.trim()) {
            throw new AiProviderError('ai_settings_credentials_required');
        }

        if (!globalThis.crypto?.subtle || !globalThis.crypto.randomUUID) {
            throw new AiProviderError('ai_settings_secure_connection_required');
        }

        const key = await this.getPublicKey();
        const lifetime = Number(key?.expires_in_seconds);
        if (!key?.company_id || !Number.isFinite(lifetime) || lifetime <= 0) {
            throw new AiProviderError('ai_settings_invalid_public_key');
        }

        const issuedAt = Math.floor(Date.now() / 1000);
        const publicKey: AiCredentialPublicKey = {
            kid: key.key_id,
            kty: key.key_type,
            alg: key.algorithm,
            use: key.key_use,
            n: key.modulus,
            e: key.exponent,
        };
        const submission: AiCredentialSubmission = {
            companyId: key.company_id,
            providerId: params.ai_provider_id,
            label: params.api_key_label.trim(),
            apiKey: params.api_key,
            requestId: globalThis.crypto.randomUUID().replace(/-/g, ''),
            issuedAt,
            expiresAt: issuedAt + Math.min(lifetime, 300),
            replaceProviderKeyno: params.replaceProviderKeyno,
        };

        return encryptAiCredential({ publicKey, submission });
    }
}

export class AiProviderError extends Error {
    constructor(public readonly translationId: string) {
        super(translationId);
        this.name = 'AiProviderError';
    }
}

export interface CreateAiProviderParams {
    ai_provider_id: string;
    api_key_label: string;
    api_key: string;
}

export interface ReplaceAiProviderParams extends CreateAiProviderParams {
    ai_provider_keyno: string | number;
}

export interface DeleteAiProviderParams {
    ai_provider_keyno: string | number;
}

export interface FetchAiProviderModelsParams {
    ai_provider_keyno: string | number;
}

interface AiProviderEncryptedCredentialParams {
    encrypted_credential: string;
    ai_provider_keyno?: string | number;
}

interface AiProviderDataTaskParams {
    method: number;
    parameters?: AiProviderEncryptedCredentialParams | DeleteAiProviderParams | FetchAiProviderModelsParams;
}

interface EncryptAiProviderCredentialParams extends CreateAiProviderParams {
    replaceProviderKeyno: number;
}

export interface AiSettingsGet {
    main_tab: string;
    ai_provider_keyno: string | number | null;
    ai_provider_id: string;
    api_key_label: string;
    api_key: string;
}

/** Parameters for the provider models grid (DataTask 4040). */
export interface GetAiProviderModelsParams {
    ai_provider_keyno: string | number | null;
}

export interface AiProviderName {
    ai_provider_id: string;
    ai_provider_name: string;
}

export interface AiProvider extends AiProviderName {
    ai_provider_keyno: string | number;
    api_key_label: string;
    last_synced_datettime: string | null;
}

export interface AiProviderAction {
    ai_provider_keyno: string | number;
    api_key_label: string;
    credential_ref: string;
    state: string;
}

export interface AiProviderPublicKey {
    key_id: string;
    key_type: string;
    algorithm: string;
    key_use: string;
    modulus: string;
    exponent: string;
    company_id: string | number;
    expires_in_seconds: string | number;
}

export interface AiCredentialSubmission {
    companyId: string | number;
    providerId: string;
    label: string;
    apiKey: string;
    requestId: string;
    issuedAt: number;
    expiresAt: number;
    replaceProviderKeyno: number;
}

export interface AiCredentialPublicKey extends JsonWebKey {
    kid: string;
}

export interface EncryptAiCredentialParams {
    publicKey: AiCredentialPublicKey;
    submission: AiCredentialSubmission;
    cryptoApi?: Crypto;
}

/**
 * Encrypts a credential submission as a compact RSA-OAEP-256/A256GCM JWE.
 *
 * @param params the Agent publicKey, credential submission and optional
 * cryptoApi implementation (defaults to browser Web Crypto).
 * @returns promise containing the five-part compact JWE, including its
 * protected header, encrypted AES key, IV, ciphertext and authentication tag.
 */
export async function encryptAiCredential(params: EncryptAiCredentialParams): Promise<string> {
    const { publicKey, submission, cryptoApi = globalThis.crypto } = params;

    if (!cryptoApi?.subtle || publicKey.kty !== 'RSA' || publicKey.alg !== 'RSA-OAEP-256' || !publicKey.kid || !publicKey.n || !publicKey.e) {
        throw new AiProviderError('ai_settings_public_key_crypto_required');
    }
    const utf8 = new TextEncoder();

    const base64Url = (bytes: Uint8Array): string => {
        let binary = '';
        for (const byte of bytes) {
            binary += String.fromCharCode(byte);
        }

        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    };
    const rsaKey = await cryptoApi.subtle.importKey(
        'jwk',
        {
            kty: 'RSA',
            n: publicKey.n,
            e: publicKey.e,
            ext: true,
            key_ops: ['encrypt'],
        },
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt'],
    );
    const aesKey = await cryptoApi.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
    const rawKey = new Uint8Array(await cryptoApi.subtle.exportKey('raw', aesKey));
    const iv = cryptoApi.getRandomValues(new Uint8Array(12));
    const header = base64Url(
        utf8.encode(
            JSON.stringify({
                alg: 'RSA-OAEP-256',
                enc: 'A256GCM',
                kid: publicKey.kid,
                typ: 'insoft-credential+jwe',
            }),
        ),
    );
    const plaintext = utf8.encode(JSON.stringify(submission));
    try {
        const encryptedKey = new Uint8Array(await cryptoApi.subtle.encrypt({ name: 'RSA-OAEP' }, rsaKey, rawKey));
        const encryptedPayload = new Uint8Array(
            await cryptoApi.subtle.encrypt(
                {
                    name: 'AES-GCM',
                    iv,
                    additionalData: utf8.encode(header),
                    tagLength: 128,
                },
                aesKey,
                plaintext,
            ),
        );

        return [header, base64Url(encryptedKey), base64Url(iv), base64Url(encryptedPayload.subarray(0, -16)), base64Url(encryptedPayload.subarray(-16))].join('.');
    } finally {
        rawKey.fill(0);
        plaintext.fill(0);
    }
}
